import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import translations from '@translationKeys'

import { AbsoluteCenterWrapper } from '@components/AbsoluteCenterWrapper'
import CustomPopover, { CustomPopoverProps } from '@components/CustomPopover'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { useChatContext } from '@modules/chat/useChatContext'
import contactsApi from '@modules/contacts/api'
import useMeetingMessages, { SanitizedPages, useChatMessages } from '@modules/contacts/hooks/useMeetingMessages'
import { Meeting, MeetingOverview, MeetingStatus } from '@modules/contacts/types'
import { preparePendoCorporateHistoryEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoCorporateHistoryEventType, PendoEventType } from '@modules/pendo/types'
import { Company } from '@modules/search/types'
import CloseIcon from '@mui/icons-material/Close'
import { Button } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import DateRangeSlider from '../DateRangeSlider'
import MessageStreamList from './MessageStreamList'
import {
  CloseButtonWrapper,
  MessageStreamPopoverContent,
  MessageStreamTitleContainer,
  MessageStreamWrapper,
} from './index.styles'
import { getSliderValueFromDate } from './utils'

export type MessageStreamPopoverProps = { recentMeeting: Meeting; companyId: Company['id'] } & Pick<
  CustomPopoverProps,
  'anchor' | 'setAnchor'
>

const ContactsHistoryPopover = ({ recentMeeting, companyId, anchor, setAnchor }: MessageStreamPopoverProps) => {
  // eslint-disable-next-line
  const [selectedMeeting, setSelectedMeeting] = useState(recentMeeting)
  const [selectedDate, setSelectedDate] = useState(recentMeeting.date)
  const [selectedYear, setSelectedYear] = useState<number>(DateTime.fromISO(selectedDate).year)
  const [sliderValue, setSliderValue] = useState(getSliderValueFromDate(selectedDate))
  const { t } = useTranslation()
  const { openChatModalByChatId } = useChatContext()

  const { user } = useAuthContext()
  const { trackPendoEvent } = usePendoTrackEvent()
  const location = useLocation()
  const { isMobile } = useScreenDimensions()

  const { data: years = [] } = useQuery({
    queryKey: [contactsApi.getContactYears.key, companyId],
    queryFn: () => contactsApi.getContactYears.fn({ companyId }),
    placeholderData: keepPreviousData,
    enabled: !!companyId,
  })

  const adjustedYears = useMemo(() => {
    if (!years.length) {
      return [DateTime.fromISO(recentMeeting.date).year]
    }

    return years
  }, [years, recentMeeting])

  const { data: meetings } = useQuery({
    queryKey: [contactsApi.getContactsForAYear.key, selectedYear, companyId],
    queryFn: () => contactsApi.getContactsForAYear.fn({ companyId, year: selectedYear }),
    placeholderData: keepPreviousData,
    enabled: !!companyId && !!selectedMeeting && !!selectedYear,
  })

  const {
    data: dayMeetings = [],
    isLoading: isLoadingDayMeetings,
    isRefetching: isRefetchingDayMeetings,
  } = useQuery({
    queryKey: [contactsApi.getContactsForADay.key, DateTime.fromISO(selectedDate).toISODate(), companyId],
    queryFn: () => contactsApi.getContactsForADay.fn({ companyId, date: selectedDate }),
    enabled: !!companyId && !!selectedDate,
  })

  useEffect(() => {
    if (meetings?.length) {
      const nextUpcomingMeeting = meetings.reduce((prevMeeting, currentMeeting) => {
        if (!prevMeeting || DateTime.fromISO(currentMeeting.date) > DateTime.fromISO(prevMeeting.date)) {
          return currentMeeting
        }

        return prevMeeting
      }, null as unknown as MeetingOverview)

      setSelectedDate(nextUpcomingMeeting.date)
      setSliderValue(getSliderValueFromDate(nextUpcomingMeeting.date))
    }
  }, [meetings])

  const sendPendoEvent = useCallback(
    (data: { eventName?: string; date: string; meetingStatus?: string; eventType: PendoCorporateHistoryEventType }) => {
      const { eventName, date, meetingStatus, eventType } = data
      const pendoTrackingEvent = preparePendoCorporateHistoryEvent({
        user,
        meetingStatus,
        date,
        eventName,
        location: location.pathname,
        eventType,
      })
      trackPendoEvent(PendoEventType.CorporateHistory, pendoTrackingEvent)
    },
    [location.pathname, user, trackPendoEvent],
  )

  useEffect(() => {
    sendPendoEvent({
      eventName: recentMeeting.event?.name,
      date: recentMeeting.date,
      meetingStatus: recentMeeting.status,
      eventType: PendoCorporateHistoryEventType.DefaultMeeting,
    })
  }, [recentMeeting?.id, recentMeeting.date, recentMeeting.event?.name, recentMeeting.status, sendPendoEvent])

  useEffect(() => {
    if (dayMeetings?.length) {
      setSelectedMeeting(dayMeetings[0])
    }

    const firstMeeting = dayMeetings?.length === 1 ? dayMeetings[0] : undefined
    sendPendoEvent({
      eventName: firstMeeting?.event?.name,
      date: selectedDate,
      meetingStatus: firstMeeting?.status,
      eventType: PendoCorporateHistoryEventType.DateSelection,
    })
  }, [dayMeetings, selectedDate, sendPendoEvent])

  const streamId = useMemo(() => {
    return meetings?.find(meeting => meeting.id === selectedMeeting.id)?.streamId
  }, [meetings, selectedMeeting])

  const {
    data: rawData,
    isFetchingNextPage,
    fetchNextPage,
  } = useMeetingMessages({
    eventId: selectedMeeting?.event?.id,
    streamId: streamId as string,
  })

  const chatId = useMemo(
    () => (selectedMeeting.status === MeetingStatus.INMAIL ? selectedMeeting.id : undefined),
    [selectedMeeting],
  )

  const chatMessagesData = useChatMessages({ chatId })

  // it loads either useMeetingMessages via streamId or useChatMessages via chatId, it is not possible to load both
  const data = (chatId ? chatMessagesData : rawData ?? {}) as SanitizedPages

  const CloseIconSection = (
    <CloseButtonWrapper onClick={() => setAnchor(null)}>
      <Typography variant="body1">{t(translations.COMMON_CLOSE)}</Typography>
      <CloseIcon sx={{ width: '14px', height: '14px' }} />
    </CloseButtonWrapper>
  )

  return (
    <CustomPopover id="contacts-history-popover" anchor={anchor} setAnchor={setAnchor}>
      <Box display="flex" flexWrap={isMobile ? 'wrap' : 'initial'}>
        {meetings && (
          <DateRangeSlider
            companyId={companyId}
            sliderValue={sliderValue}
            onSliderValueChange={setSliderValue}
            meetings={meetings}
            dayMeetings={dayMeetings}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            selectedYear={selectedYear}
            onYearSelect={setSelectedYear}
            selectedMeeting={selectedMeeting}
            setSelectedMeeting={setSelectedMeeting}
            years={adjustedYears}
            isLoading={isLoadingDayMeetings || isRefetchingDayMeetings}
            closeIcon={isMobile ? CloseIconSection : undefined}
          />
        )}

        <MessageStreamPopoverContent data-testid="message-stream-wrapper">
          <MessageStreamWrapper>
            <MessageStreamTitleContainer>
              <Typography data-testid="message-stream-title" variant="h3">
                {!!data.subject && `Subject: ${data.subject}`}
              </Typography>
              {!isMobile && CloseIconSection}
            </MessageStreamTitleContainer>
            <MessageStreamList
              hasNextPage={data.hasMore}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              messages={data.messageStreamItems ?? []}
            />
            {chatId && (
              <AbsoluteCenterWrapper>
                <Button
                  variant="contained"
                  data-testid="contact_btn"
                  onClick={() => {
                    openChatModalByChatId(chatId)
                    setAnchor(null)
                  }}
                  sx={{ marginTop: '200px' }}
                >
                  {t(translations.SEE_ALL_MESSAGES)}
                </Button>
              </AbsoluteCenterWrapper>
            )}
          </MessageStreamWrapper>
        </MessageStreamPopoverContent>
      </Box>
    </CustomPopover>
  )
}

export default ContactsHistoryPopover
